<template>
  <div>
    <div class="expiry-data d-flex justify-content-between align-items-center flex-wrap mb-3">
      <div class="d-flex align-items-center p-0">
        <h2 class="text-left mb-0">
          {{ editor.name || $t("formsEditor.untitled") }}
        </h2>
        <svg
          @click="openEditNameModal"
          class="ml-3 cursor-pointer"
          :title="$t('formsEditor.templateName')"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.52719 16.7477L14.5274 7.74753L10.2525 3.4726L1.25226 12.4728C1.12836 12.5969 1.04034 12.7522 0.997548 12.9222L0 18L5.07684 17.0025C5.2473 16.9598 5.40322 16.8717 5.52719 16.7477V16.7477ZM17.4329 4.84205C17.796 4.47881 18 3.98622 18 3.4726C18 2.95898 17.796 2.46639 17.4329 2.10315L15.8968 0.567123C15.5336 0.203994 15.041 0 14.5274 0C14.0138 0 13.5212 0.203994 13.1579 0.567123L11.6219 2.10315L15.8968 6.37808L17.4329 4.84205Z"
            fill="currentColor" />
        </svg>
        <span class="status-chip ml-3" :class="statusClass">{{ statusLabel }}</span>
      </div>
      <!-- Saving and publishing are the two things a template is for, so they
           read as buttons up here rather than hiding in a row menu. -->
      <div class="d-flex align-items-center flex-wrap">
        <!-- <b-button
          variant="secondary"
          class="mx-0 h-40px px-3 mr-2 d-flex justify-content-center align-items-center f-14"
          @click="saveTemplate()"
        >
          {{ $t("formsEditor.saveDraft") }}
        </b-button> -->
        <!-- Publishing acts on the saved record, so a template that was never
             saved has nothing to publish yet. -->
        <b-button
          v-if="editor.id"
          variant="primary"
          class="export equipment d-flex justify-content-center align-items-center px-3 h-40px"
          @click="togglePublish"
        >
          <p class="mb-0 text-white p-0">
            {{ isPublished ? $t("formsEditor.unpublish") : $t("formsEditor.publish") }}
          </p>
        </b-button>
      </div>
    </div>

    <!-- ── ACCESS ── -->
    <div class="setting-card mb-3">
      <!-- Title, pickers, the line explaining the chosen mode and the assign
           actions all sit on one row; it wraps only on narrow screens. -->
      <div class="access-row px-0">
        <h4 class="mb-0 pl-0 access-title">{{ $t("formsEditor.access") }}</h4>
        <b-form-select
          v-model="editor.access_mode"
          class="input f-12 access-control"
          :options="accessModeOptions"
          value-field="value"
          text-field="text"
        ></b-form-select>
        <!-- Drivers and tags both pick in the modal, so the row stays one line
             however many are chosen. -->
        <template v-if="editor.access_mode === 'specific' || editor.access_mode === 'tags'">
          <b-button
            variant="outline-primary"
            class="mx-0 h-40px px-3 d-flex align-items-center f-14"
            @click="openAccessModal"
          >
            {{ accessPickerLabel }}
          </b-button>
          <span class="access-chip" v-for="entry in selectedAccessChips" :key="entry.id">
            {{ entry.label }}
          </span>
        </template>
        <p class="chk-sub mb-0 access-hint">{{ accessModeHint }}</p>

        <div
          class="d-flex align-items-center ml-auto"
        >
          
          <!-- The tooltip sits on the wrapper: a disabled button swallows hover, so it
               would never fire on the button itself. -->
          <span v-b-tooltip.hover="isPublished ? '' : $t('formsEditor.publishToAssign')">
            <b-button
              variant="outline-primary"
              class="mx-0 h-40px px-3 d-flex align-items-center f-14 text-nowrap"
              @click="openAssignDrawer"
              :disabled="!isPublished"
            >
              <svg
                class="mr-2"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 5V19M5 12H19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              {{ $t("formsEditor.assignUsers") }}
            </b-button>
          </span>
        </div>
      </div>
    </div>

    <!-- ── APPROVALS ── -->
    <div class="setting-card mb-3">
      <div class="d-flex align-items-center">
        <b-form-checkbox v-model="editor.require_approvals" class="text-left mr-2" switch></b-form-checkbox>
        <div>
          <span class="chk-label">{{ $t("formsEditor.requireApprovals") }}</span>
          <span class="chk-sub">{{ $t("formsEditor.requireApprovalsHint") }}</span>
        </div>
      </div>
    </div>

    <!-- ── PALETTE + CANVAS ── -->
    <div class="editor-layout">
      <div class="palette-col">
        <div class="setting-card palette-card">
        <h4 class="mb-0 pl-2">{{ $t("formsEditor.paletteTitle") }}</h4>
          <!-- Sortable clones out of this list, so the palette itself never changes. -->
          <div class="palette-list" ref="palette">
            <div
              v-for="type in fieldTypes"
              :key="type.value"
              class="palette-item"
              :data-type="type.value"
              :title="$t('formsEditor.dragToAdd')"
              @click="addBlock(type.value)"
            >
              <span v-if="typeSvg(type.value)" class="type-svg" v-html="typeSvg(type.value)"></span>
              <span v-else class="type-icon" :style="typeIconStyle(type.value)">
                <b-icon :icon="type.icon" aria-hidden="true"></b-icon>
              </span>
              <span class="f-12">{{ type.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="canvas-col" ref="sectionsList">
        <!-- Collected with every submission, so it opens the form and can't be
             edited, reordered or dragged into. -->
        <div class="general-card">
          <div class="d-flex align-items-start">
            <span class="general-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="6.75" width="11" height="8" rx="2" stroke="currentColor" stroke-width="1.5" />
                <path d="M5.25 6.75V4.75C5.25 3.23 6.48 2 8 2C9.52 2 10.75 3.23 10.75 4.75V6.75"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </span>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center flex-wrap">
                <span class="general-title mr-2">{{ $t("formsEditor.generalTitle") }}</span>
                <span class="general-tag">{{ $t("formsEditor.generalTag") }}</span>
              </div>
              <p class="general-hint mb-0">{{ $t("formsEditor.generalHint") }}</p>
            </div>
          </div>
          <div class="auto-row" v-for="field in autoFields" :key="field.key">
            <span class="auto-name">{{ field.label }}</span>
            <span class="auto-tag">
              <svg class="mr-1" width="12" height="12" viewBox="0 0 16 16" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="6.75" width="11" height="8" rx="2" stroke="currentColor" stroke-width="1.5" />
                <path d="M5.25 6.75V4.75C5.25 3.23 6.48 2 8 2C9.52 2 10.75 3.23 10.75 4.75V6.75"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              {{ $t("formsEditor.autoCollected") }}
            </span>
          </div>
        </div>
        <div v-for="(section, sectionIndex) in editor.sections" :key="section.id" :data-index="sectionIndex"
          :class="isTable(section) ? 'section-card table-card' : 'section-card'">
          <!-- A table asks the same questions once per row, so its columns are
               edited here and answered on the form. -->
          <template v-if="isTable(section)">
            <div class="section-head">
              <span class="drag-handle section-drag" :title="$t('formsEditor.dragToReorder')">
                <svg width="4" height="18" viewBox="0 0 4 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                  <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                  <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
                </svg>
              </span>
              <span class="section-chip">{{ $t("formsEditor.tableChip") }}</span>
              <b-form-input v-model="section.title" :placeholder="$t('formsEditor.tableTitlePlaceholder')"
                class="unit flex-grow-1"></b-form-input>
              <span class="icon-btn" :title="$t('formsEditor.deleteTable')" @click="removeSection(sectionIndex)">
                <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                    stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                    stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
            </div>
            <!-- What the table will look like on the form: the column titles and
                 a couple of empty rows. -->
            <div class="table-preview-wrap">
              <div class="table-scroll">
                <table class="table-preview">
                  <thead>
                    <tr>
                      <th v-for="column in section.columns" :key="`${section.id}-p-${column.id}`">
                        {{ column.label || $t("formsEditor.columnLabelPlaceholder") }}
                      </th>
                      <th v-if="!section.columns.length">{{ $t("formsEditor.noColumns") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in 2" :key="`${section.id}-pr-${row}`">
                      <td v-for="column in section.columns" :key="`${section.id}-pc-${row}-${column.id}`"></td>
                      <td v-if="!section.columns.length"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- <div class="d-flex align-items-center flex-wrap mt-3">
                <b-form-checkbox v-model="section.autofill" class="text-left mr-2" switch
                  @change="onAutofillToggle(section, $event)"></b-form-checkbox>
                <span class="f-12 text-grey mr-2">{{ $t("formsEditor.autofillFrom") }}</span>
                <b-form-select class="f-12 autofill-select" :options="autofillOptions"
                  :disabled="!section.autofill" v-model="section.autofill_source"></b-form-select>
              </div> -->
            </div>
            <!-- Nothing to show, and nothing to reorder, until the table has a
                 question of its own. -->
            <div v-if="section.columns.length" class="section-body-wrap">
              <div class="section-body" ref="columnBodies" :data-columns="sectionIndex">
                <div v-for="column in section.columns" :key="column.id" class="field-row" :data-id="column.id">
                  <span class="drag-handle" :title="$t('formsEditor.dragToReorder')">
                    <svg width="4" height="18" viewBox="0 0 4 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                      <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                      <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
                    </svg>
                  </span>
                  <span v-if="typeSvg(column.type)" class="type-svg" v-html="typeSvg(column.type)"></span>
                  <span v-else class="type-icon" :style="typeIconStyle(column.type)">
                    <b-icon :icon="typeMeta(column.type).icon" aria-hidden="true"></b-icon>
                  </span>
                  <div class="field-main">
                    <div class="d-flex align-items-center flex-wrap">
                      <!-- The type sits on its own line at full width, so the
                           label must not reserve a gutter beside it. -->
                      <b-form-input v-model="column.label" :placeholder="$t('formsEditor.columnLabelPlaceholder')"
                        class="unit w-100 mb-2"></b-form-input>
                      <!-- A column's type is changeable in place, unlike a field. -->
                      <b-form-select class="f-12 column-type" :options="columnTypeOptions"
                        :value="column.type" @change="setColumnType(column, $event)"></b-form-select>
                    </div>
                    <div v-if="isChoice(column.type)" class="option-row">
                      <span v-for="(option, optionIndex) in column.options" :key="`${column.id}-o-${optionIndex}`"
                        class="option-chip">
                        {{ option || $t("formsEditor.optionPlaceholder") }}
                        <span class="cursor-pointer d-inline-flex" :title="$t('formsEditor.removeOption')"
                          @click="removeOption(column, optionIndex)">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5"
                              stroke-linecap="round" />
                          </svg>
                        </span>
                      </span>
                      <b-button variant="link" class="p-0 f-12 add-option d-flex align-items-center"
                        @click="openOptionsModal(column)">
                        <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                        {{ $t("formsEditor.editAnswers") }}
                      </b-button>
                    </div>
                  </div>
                  <b-form-checkbox v-model="column.required" class="text-left required-chk" switch>
                    <span class="f-12">{{ $t("formsEditor.required") }}</span>
                  </b-form-checkbox>
                  <span class="icon-btn" :title="$t('formsEditor.deleteColumn')"
                    @click="removeColumn(section, column.id)">
                    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path
                        d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                        stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path
                        d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                        stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div class="section-foot d-flex justify-content-center" :class="section.columns.length ? '':'b-t-0'">
              <b-dropdown class="field-picker-toggle" variant="outline-primary" no-caret
                toggle-class="h-40px px-3 d-flex align-items-center f-14" menu-class="field-picker-menu">
                <template #button-content>
                  <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                  </svg>
                  {{ $t("formsEditor.addQuestion") }}
                </template>
                <div class="field-picker">
                  <button v-for="type in columnTypes" :key="`${section.id}-c-${type.value}`" type="button"
                    class="field-picker-item" @click="addColumn(section, type.value)">
                    <span v-if="typeSvg(type.value)" class="type-svg" v-html="typeSvg(type.value)"></span>
                    <span v-else class="type-icon" :style="typeIconStyle(type.value)">
                      <b-icon :icon="type.icon" aria-hidden="true"></b-icon>
                    </span>
                    <span class="f-12">{{ type.label }}</span>
                  </button>
                </div>
              </b-dropdown>
            </div>
          </template>
          <template v-else>
          <div class="section-head">
            <span class="drag-handle section-drag" :title="$t('formsEditor.dragToReorder')">
              <svg width="4" height="18" viewBox="0 0 4 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
              </svg>
            </span>
            <span class="section-chip">{{ $t("formsEditor.sectionChip") }}</span>
            <b-form-input
              v-model="section.title"
              :placeholder="$t('formsEditor.sectionTitlePlaceholder')"
              class="unit flex-grow-1"
            ></b-form-input>
            <span
              v-if="editor.sections.length > 1"
              class="icon-btn"
              :title="$t('formsEditor.deleteSection')"
              @click="removeSection(sectionIndex)"
            >
              <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path
                  d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                  stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path
                  d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                  stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </span>
          </div>
          <div class="section-body-wrap">
            <p v-if="!section.blocks.length" class="empty-hint">{{ $t("formsEditor.noBlocks") }}</p>
            <div class="section-body" ref="sectionBodies" :data-section="sectionIndex">
              <div v-for="block in section.blocks" :key="block.id" class="field-row" :data-id="block.id">
                <span class="drag-handle" :title="$t('formsEditor.dragToReorder')">
                  <svg width="4" height="18" viewBox="0 0 4 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
              </svg>
                </span>
                <span v-if="typeSvg(block.type)" class="type-svg" v-html="typeSvg(block.type)"></span>
                <span v-else class="type-icon" :style="typeIconStyle(block.type)">
                  <b-icon :icon="typeMeta(block.type).icon" aria-hidden="true"></b-icon>
                </span>
                <div class="field-main">
                  <b-form-input
                    v-if="block.type !== 'instructions'"
                    v-model="block.label"
                    :placeholder="$t('formsEditor.fieldLabelPlaceholder')"
                    class="unit"
                  ></b-form-input>
                  <!-- Instructions are read-only text for whoever fills the form,
                       so they carry a formatted body instead of an answer. -->
                  <vue-editor
                    v-if="block.type === 'instructions'"
                    class="instructions-editor"
                    v-model="block.body"
                    :editor-toolbar="instructionsToolbar"
                    :placeholder="$t('formsEditor.instructionsPlaceholder')"
                  />
                  <!-- A media question takes one file or a set of them; the
                       driver's uploader follows whichever is chosen here. -->
                  <b-form-select
                    v-if="block.type === 'photo'"
                    class="input f-12 media-count-select mt-2"
                    :value="block.multiple === true"
                    :options="mediaCountOptions"
                    @change="$set(block, 'multiple', $event)"
                  ></b-form-select>
                  <!-- Out of the chain above so it can read under the picker. -->
                  <p v-if="block.type !== 'instructions' && typeHint(block.type)" class="field-hint">
                    {{ typeHint(block.type) }}
                  </p>
                  <!-- Answers are typed in a modal; the row just lists them. -->
                  <div v-if="isChoice(block.type)" class="option-row">
                    <span v-for="(option, optionIndex) in block.options" :key="`${block.id}-o-${optionIndex}`"
                      class="option-chip">
                      {{ option || $t("formsEditor.optionPlaceholder") }}
                      <span class="cursor-pointer d-inline-flex" :title="$t('formsEditor.removeOption')"
                        @click="removeOption(block, optionIndex)">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5"
                            stroke-linecap="round" />
                        </svg>
                      </span>
                    </span>
                    <b-button variant="link" class="p-0 f-12 ml-2 add-option d-flex align-items-center"
                      @click="openOptionsModal(block)">
                      <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                      </svg>
                      {{ $t("formsEditor.editAnswers") }}
                    </b-button>
                  </div>
                  <!-- Answering this question one way can reveal further fields;
                       they ride along on the block as `conditional_logic`. -->
                  <div v-if="isChoice(block.type) && !block.conditional_logic" class="d-flex justify-content-center mt-3">
                    <b-button variant="outline-primary"
                      class="mx-0 h-40px px-3 d-flex align-items-center f-14 w-fit-content"
                      @click="addConditionalLogic(block)">
                      <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                      </svg>
                      {{ $t("formsEditor.addConditionalLogic") }}
                    </b-button>
                  </div>
                  <div v-if="isChoice(block.type) && block.conditional_logic" class="cond-card mt-2">
                    <div class="d-flex align-items-center justify-content-between">
                      <h5 class="cond-title mb-0">{{ $t("formsEditor.showFieldsWhen") }}</h5>
                      <span class="icon-btn" :title="$t('formsEditor.removeCondition')"
                        @click="removeConditionalLogic(block)">
                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                          <path
                            d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                            stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          <path
                            d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                            stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                          <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                        </svg>
                      </span>
                    </div>
                    <div class="cond-row">
                      <span class="cond-label">{{ $t("formsEditor.condition") }}</span>
                      <span class="cond-line"></span>
                      <b-form-select class="f-12 cond-control" v-model="block.conditional_logic.condition"
                        :options="conditionOptions"></b-form-select>
                    </div>
                    <div class="cond-row">
                      <span class="cond-label">{{ $t("formsEditor.conditionValue") }}</span>
                      <span class="cond-line"></span>
                      <!-- The value is always one of this question's own options. -->
                      <b-form-select class="f-12 cond-control" v-model="block.conditional_logic.value"
                        :options="valueOptions(block)"></b-form-select>
                    </div>
                    <div class="cond-body-wrap">
                      <div class="cond-body" ref="condBodies" :data-cond="`${sectionIndex}|${block.id}`">
                        <div v-for="nested in block.conditional_logic.fields" :key="nested.id" class="field-row"
                          :data-id="nested.id">
                          <span class="drag-handle" :title="$t('formsEditor.dragToReorder')">
                            <svg width="4" height="18" viewBox="0 0 4 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                              <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                              <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
                            </svg>
                          </span>
                          <span v-if="typeSvg(nested.type)" class="type-svg" v-html="typeSvg(nested.type)"></span>
                          <span v-else class="type-icon" :style="typeIconStyle(nested.type)">
                            <b-icon :icon="typeMeta(nested.type).icon" aria-hidden="true"></b-icon>
                          </span>
                          <div class="field-main">
                            <b-form-input v-if="nested.type !== 'instructions'" v-model="nested.label"
                              :placeholder="$t('formsEditor.fieldLabelPlaceholder')" class="unit"></b-form-input>
                            <vue-editor v-if="nested.type === 'instructions'" class="instructions-editor mt-2"
                              v-model="nested.body" :editor-toolbar="instructionsToolbar"
                              :placeholder="$t('formsEditor.instructionsPlaceholder')" />
                            <p v-else-if="typeHint(nested.type)" class="field-hint">{{ typeHint(nested.type) }}</p>
                            <div v-if="isChoice(nested.type)" class="option-row">
                              <span v-for="(option, optionIndex) in nested.options"
                                :key="`${nested.id}-o-${optionIndex}`" class="option-chip">
                                {{ option || $t("formsEditor.optionPlaceholder") }}
                                <span class="cursor-pointer d-inline-flex"
                                  :title="$t('formsEditor.removeOption')"
                                  @click="removeOption(nested, optionIndex)">
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5"
                                      stroke-linecap="round" />
                                  </svg>
                                </span>
                              </span>
                              <b-button variant="link" class="p-0 f-12 add-option d-flex align-items-center"
                                @click="openOptionsModal(nested)">
                                <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                {{ $t("formsEditor.editAnswers") }}
                              </b-button>
                            </div>
                            <div v-if="isCombo(nested.type)" class="sub-fields">
                              <p class="f-12 text-grey mb-2">{{ $t("formsEditor.subFieldsHint") }}</p>
                              <div class="sub-grid">
                                <b-form-checkbox v-for="key in subFieldKeys(nested.type)"
                                  :key="`${nested.id}-s-${key}`" class="text-left pl-0 f-12"
                                  :checked="hasSubField(nested, key)"
                                  @change="toggleSubField(nested, key, $event)">
                                  <span class="chk-label">{{ $t(`formsEditor.sub_${key}`) }}</span>
                                </b-form-checkbox>
                              </div>
                            </div>
                          </div>
                          <b-form-checkbox v-if="nested.type !== 'instructions'" v-model="nested.required"
                            class="text-left required-chk" switch>
                            <span class="f-12">{{ $t("formsEditor.required") }}</span>
                          </b-form-checkbox>
                          <span class="icon-btn" :title="$t('formsEditor.deleteField')"
                            @click="removeConditionalField(block, nested.id)">
                            <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                              <path
                                d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                                stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path
                                d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                                stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                              <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="d-flex justify-content-center">
                    <b-dropdown class="field-picker-toggle" variant="outline-primary" no-caret
                      toggle-class="h-40px px-3 d-flex align-items-center f-14" menu-class="field-picker-menu">
                      <template #button-content>
                        <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5"
                            stroke-linecap="round" />
                        </svg>
                        {{ $t("formsEditor.noConditionalFields") }}
                      </template>
                      <div class="field-picker">
                        <button v-for="type in fieldTypes" :key="`${block.id}-p-${type.value}`" type="button"
                          class="field-picker-item" @click="addConditionalField(block, type.value)">
                          <span v-if="typeSvg(type.value)" class="type-svg" v-html="typeSvg(type.value)"></span>
                          <span v-else class="type-icon" :style="typeIconStyle(type.value)">
                            <b-icon :icon="type.icon" aria-hidden="true"></b-icon>
                          </span>
                          <span class="f-12">{{ type.label }}</span>
                        </button>
                      </div>
                    </b-dropdown>
                    </div>
                  </div>
                  <div v-if="isCombo(block.type)" class="sub-fields">
                    <p class="f-12 text-grey mb-2">{{ $t("formsEditor.subFieldsHint") }}</p>
                    <div class="sub-grid">
                      <b-form-checkbox
                        v-for="key in subFieldKeys(block.type)"
                        :key="`${block.id}-s-${key}`"
                        class="text-left pl-0 f-12"
                        :checked="hasSubField(block, key)"
                        @change="toggleSubField(block, key, $event)"
                      >
                        <span class="chk-label">{{ $t(`formsEditor.sub_${key}`) }}</span>
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
                <b-form-checkbox
                  v-if="block.type !== 'instructions'"
                  v-model="block.required"
                  class="text-left required-chk"
                  switch
                >
                  <span class="f-12">{{ $t("formsEditor.required") }}</span>
                </b-form-checkbox>
                <span
                  class="icon-btn"
                  :title="$t('formsEditor.deleteField')"
                  @click="removeBlock(sectionIndex, block.id)"
                >
                  <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path
                      d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                      stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                      d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                      stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div class="section-foot d-flex justify-content-center">
            <b-dropdown class="field-picker-toggle" variant="outline-primary" no-caret
              toggle-class="h-40px px-3 d-flex align-items-center f-14" menu-class="field-picker-menu">
              <template #button-content>
                <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
                {{ $t("formsEditor.addField") }}
              </template>
              <div class="field-picker">
                <button v-for="type in fieldTypes" :key="`${section.id}-p-${type.value}`" type="button"
                  class="field-picker-item" @click="addSectionField(section, type.value)">
                  <span v-if="typeSvg(type.value)" class="type-svg" v-html="typeSvg(type.value)"></span>
                  <span v-else class="type-icon" :style="typeIconStyle(type.value)">
                    <b-icon :icon="type.icon" aria-hidden="true"></b-icon>
                  </span>
                  <span class="f-12">{{ type.label }}</span>
                </button>
              </div>
            </b-dropdown>
          </div>
          </template>
        </div>
        <!-- The two ways to add a container, side by side. -->
        <div class="add-row">
          <button type="button" class="dashed-btn" @click="addSection">
            <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
            {{ $t("formsEditor.addSection") }}
          </button>
          <button type="button" class="dashed-btn" @click="addTable">
            <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
            {{ $t("formsEditor.addTable") }}
          </button>
        </div>
        <!-- Save and Close belong to the form column, not the page: below both
             columns they would end the palette's travel and unstick it. -->
        <div class="d-flex justify-content-end align-items-center flex-wrap mt-auto pt-4">
          <b-button
            variant="secondary"
            class="mx-0 h-40px w-100px mr-2 d-flex justify-content-center align-items-center f-14"
            @click="$emit('close')"
          >
            {{ $t("formsEditor.close") }}
          </b-button>
          <b-button
            variant="primary"
            class="export equipment d-flex justify-content-center align-items-center w-100px h-40px"
            @click="saveTemplate()"
          >
            <p class="mb-0 text-white p-0">{{ $t("formsEditor.save") }}</p>
          </b-button>
        </div>
      </div>
    </div>

    <!-- The same drawer the templates list opens, so assigning works the same
         from either place. -->
    <AssignDriversDrawer v-model="assignDrawer" :template-id="editor.id" @assigned="loadAssigned" />

    <DeleteModal v-if="rowToRemove" :message="removeAssignedMessage" @confirm="removeAssigned($event)" />

    <b-modal
      id="modal-select-access"
      centered
      no-close-on-backdrop
      no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :body-class="'px-3 pt-2 pb-4'"
      :header-class="'align-items-center'"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ accessPickerLabel }}
        </h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <!-- Who can find the form is a short list of picks, so it stays a
           searchable chip picker; assigning is where the ticked list lives. -->
      <div class="text-left select_arrow">
        <label class="state">{{ accessPickerLabel }}</label>
        <v-select
          multiple
          class="bg_white w-100 f-12"
          v-model="accessModalSelected"
          :options="accessPickerOptions"
          item-value="id"
          item-text="label"
          :reduce="(option) => option.id"
          @open="onAccessSearch('')"
          @search="onAccessSearch"
        >
          <template #no-options>
            {{ editor.access_mode === "tags" ? $t("formsEditor.noTagsFound") : $t("formsEditor.searchDrivers") }}
          </template>
        </v-select>
        <p class="f-12 text-grey mb-0 mt-2">
          {{ $t("formsEditor.selectedCount", { count: accessModalSelected.length }) }}
        </p>
      </div>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary" @click="closeAccessModal">
            {{ $t("formsEditor.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px px-3 m-0" variant="primary" @click="confirmAccessSelection">
            {{ $t("formsEditor.confirmSelection") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit-answers"
      centered
      no-close-on-backdrop
      no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :body-class="'px-3 pt-2'"
      :header-class="'align-items-center'"
      scrollable
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t("formsEditor.editAnswers") }}
        </h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <div v-for="(option, optionIndex) in optionsDraft" :key="`answer-${optionIndex}`"
        class="d-flex align-items-center mb-2">
        <b-form-input
          :value="option"
          :placeholder="$t('formsEditor.optionPlaceholder')"
          class="unit"
          @input="setDraftOption(optionIndex, $event)"
        ></b-form-input>
        <span class="icon-btn" :title="$t('formsEditor.removeOption')" @click="removeDraftOption(optionIndex)">
          <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
              stroke-linejoin="round" />
            <path
              d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
              stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path
              d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
              stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
              stroke-linejoin="round" />
            <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </span>
      </div>
      <p v-if="optionsError" class="field-error">{{ optionsError }}</p>
      <div class="d-flex mt-3">
        <b-button variant="outline-primary" class="mx-0 h-40px px-3 d-flex align-items-center f-14"
          @click="addDraftOption">
          <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          {{ $t("formsEditor.addAnswer") }}
        </b-button>
      </div>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary" @click="closeOptionsModal">
            {{ $t("formsEditor.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="saveOptions">
            {{ $t("formsEditor.save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit-form-name"
      centered
      no-close-on-backdrop
      no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :body-class="'px-3 pt-2'"
      :header-class="'align-items-center'"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t("formsEditor.templateName") }}
        </h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <b-form-group :label="$t('formsEditor.templateName')" class="text-left mb-0">
        <b-form-input
          v-model="nameDraft"
          :placeholder="$t('formsEditor.templateNamePlaceholder')"
          class="unit"
        ></b-form-input>
        <p v-if="errors && errors.templateName" class="field-error">
          {{ errors.templateName[0] }}
        </p>
      </b-form-group>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary" @click="closeEditNameModal">
            {{ $t("formsEditor.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="saveTemplateName">
            {{ $t("formsEditor.save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import * as moment from "moment-timezone";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { VueEditor } from "vue2-editor";
import DeleteModal from "@/components/DeleteModal.vue";
import AssignDriversDrawer from "@/components/Forms/AssignDriversDrawer.vue";
import { formStatusKey, formStatusTone, isPublished } from "@/services/formStatus";
import { pickerDate } from "@/services/helper";

// Read-only text shown to whoever fills the form — it asks nothing.
const CONTENT_TYPES = ["instructions"];
// Question types that need their own answer list.
const CHOICE_TYPES = ["select", "radio", "multiselect"];
// Types that pick a record and can also collect a few of its details.
const COMBO_TYPES = ["driver", "vehicle", "trailer"];
const DRIVER_SUB_FIELD_KEYS = ["license", "license_state", "phone", "email"];
const ASSET_SUB_FIELD_KEYS = ["vin", "year", "make", "model", "plate", "plate_state"];
// Artwork supplied for the field types, keyed by type. Anything missing here
// falls back to the tinted b-icon chip.
const TYPE_SVGS = {
  instructions: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M6 16.4C6 13.383 6 11.8745 6.94627 10.9373C7.89254 10 9.41554 10 12.4615 10H13.5385C16.5845 10 18.1075 10 19.0537 10.9373C20 11.8745 20 13.383 20 16.4V19.6C20 22.617 20 24.1255 19.0537 25.0627C18.1075 26 16.5845 26 13.5385 26H12.4615C9.41554 26 7.89254 26 6.94627 25.0627C6 24.1255 6 22.617 6 19.6V16.4Z" stroke="#2D69F6" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M19.5376 20H20.4608C23.072 20 24.3776 20 25.1888 19.1799C26 18.3598 26 17.0399 26 14.4V11.6C26 8.96013 26 7.6402 25.1888 6.8201C24.3776 6 23.072 6 20.4608 6H19.5376C16.9264 6 15.6208 6 14.8096 6.8201C14.1002 7.53726 14.0112 8.63664 14 10.6667" stroke="#2D69F6" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M10 16H13M10 21H15" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"/>
<path d="M14.5 7L18.5 10.5" stroke="#2D69F6" stroke-width="1.5" stroke-linejoin="round"/>
</svg>`,
  text: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M9 12V11C9 9.89543 9.89543 9 11 9H16M23 12V11C23 9.89543 22.1046 9 21 9H16M16 9V23M16 23H13M16 23H19" stroke="#2D69F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,
  number: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M18.806 14.56L18.274 17.08H20.206V18.2H18.036L17.434 21H16.216L16.818 18.2H13.878L13.276 21H12.058L12.66 18.2H10.462V17.08H12.898L13.43 14.56H11.26V13.44H13.668L14.27 10.64H15.488L14.886 13.44H17.826L18.428 10.64H19.646L19.044 13.44H20.99V14.56H18.806ZM17.588 14.56H14.648L14.116 17.08H17.056L17.588 14.56Z" fill="#2D69F6"/>
</svg>`,
  select: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#5B38F8" fill-opacity="0.12"/>
<circle cx="16" cy="16" r="9" stroke="#5B38F8" stroke-width="1.5"/>
<circle cx="16" cy="16" r="3" fill="#5B38F8"/>
</svg>`,
  radio: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#5B38F8" fill-opacity="0.12"/>
<path d="M7 13V24C7 24.5523 7.44772 25 8 25H19" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.999 21L23.999 21C24.5513 21 24.999 20.5523 24.999 20L24.999 8C24.999 7.44772 24.5513 7 23.999 7L11.999 7C11.4467 7 10.999 7.44772 10.999 8L10.999 20C10.999 20.5523 11.4467 21 11.999 21Z" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M18.001 16.9999V10.9999" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M15.001 13.9999L21.001 13.9999" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  multiselect: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#5B38F8" fill-opacity="0.12"/>
<path d="M6.49805 15.9999C6.49805 11.5215 6.49805 9.28236 7.88929 7.89112C9.28053 6.49988 11.5197 6.49988 15.998 6.49988C20.4764 6.49988 22.7156 6.49988 24.1068 7.89112C25.498 9.28236 25.498 11.5215 25.498 15.9999C25.498 20.4782 25.498 22.7174 24.1068 24.1086C22.7156 25.4999 20.4764 25.4999 15.998 25.4999C11.5197 25.4999 9.28053 25.4999 7.88929 24.1086C6.49805 22.7174 6.49805 20.4782 6.49805 15.9999Z" stroke="#5B38F8" stroke-width="1.5"/>
<path d="M12 17.7499C12 17.7499 13.6 18.6625 14.4 19.9999C14.4 19.9999 16.8 14.7499 20 12.9999" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  date: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M22 6V8M10 6V8" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M15.9955 17H16.0045M15.9955 21H16.0045M19.991 17H20M12 17H12.009M12 21H12.009" stroke="#2D69F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7.5 12H24.5" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6.5 16.2432C6.5 11.8859 6.5 9.70728 7.75212 8.35364C9.00424 7 11.0195 7 15.05 7H16.95C20.9805 7 22.9958 7 24.2479 8.35364C25.5 9.70728 25.5 11.8859 25.5 16.2432V16.7568C25.5 21.1141 25.5 23.2927 24.2479 24.6464C22.9958 26 20.9805 26 16.95 26H15.05C11.0195 26 9.00424 26 7.75212 24.6464C6.5 23.2927 6.5 21.1141 6.5 16.7568V16.2432Z" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7 12H25" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  driver: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#FF7900" fill-opacity="0.12"/>
<path d="M16.5 26.0001H10.5909C9.04549 26.0001 7.81631 25.2481 6.71266 24.1967C4.45336 22.0442 8.1628 20.3241 9.57757 19.4817C11.6784 18.2308 13.5 17.4998 15 17.4998C16.1731 17.4998 17 17.4998 18 17.9998" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M20.5 10.5C20.5 12.9853 18.4853 15 16 15C13.5147 15 11.5 12.9853 11.5 10.5C11.5 8.01472 13.5147 6 16 6C18.4853 6 20.5 8.01472 20.5 10.5Z" stroke="#FF7900" stroke-width="1.5"/>
<path d="M22.5 18.6504C24.6263 18.6504 26.3496 20.3737 26.3496 22.5C26.3496 24.6263 24.6263 26.3496 22.5 26.3496C20.3737 26.3496 18.6504 24.6263 18.6504 22.5C18.6504 20.3737 20.3737 18.6504 22.5 18.6504Z" stroke="#FF7900" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M19.125 21.9375H22.5M25.875 21.9375H22.5M22.5 21.9375V25.875" stroke="#FF7900" stroke-width="1.3"/>
</svg>`,
  vehicle: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#FF7900" fill-opacity="0.12"/>
<path d="M6.5 16L8.5 17" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M25.5 16.5L23.5 17" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 21.5L12.2457 20.8858C12.611 19.9725 12.7937 19.5158 13.1746 19.2579C13.5555 19 14.0474 19 15.0311 19H16.9689C17.9526 19 18.4445 19 18.8254 19.2579C19.2063 19.5158 19.389 19.9725 19.7543 20.8858L20 21.5" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6 21V23.882C6 24.2607 6.24075 24.607 6.62188 24.7764C6.86918 24.8863 7.10538 25 7.39058 25H9.10942C9.39462 25 9.63082 24.8863 9.87812 24.7764C10.2592 24.607 10.5 24.2607 10.5 23.882V22" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M21.5 22V23.882C21.5 24.2607 21.7408 24.607 22.1219 24.7764C22.3692 24.8863 22.6054 25 22.8906 25H24.6094C24.8946 25 25.1308 24.8863 25.3781 24.7764C25.7592 24.607 26 24.2607 26 23.882V21" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M24 12.5L25 12" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8 12.5L7 12" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.5 13L9.5883 9.73509C10.0283 8.41505 10.2483 7.75503 10.7721 7.37752C11.2959 7 11.9916 7 13.383 7H18.617C20.0084 7 20.7041 7 21.2279 7.37752C21.7517 7.75503 21.9717 8.41505 22.4117 9.73509L23.5 13" stroke="#FF7900" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M8.5 13H23.5C24.4572 14.0135 26 15.4249 26 16.9996V20.4702C26 21.0407 25.6205 21.5208 25.1168 21.5875L22 22H10L6.88316 21.5875C6.37955 21.5208 6 21.0407 6 20.4702V16.9996C6 15.4249 7.54279 14.0135 8.5 13Z" stroke="#FF7900" stroke-width="1.5" stroke-linejoin="round"/>
</svg>`,
  trailer: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#FF7900" fill-opacity="0.12"/>
<path d="M26.4766 8.90039C26.8233 8.9006 27.0994 9.18849 27.0996 9.53809V20.3076C27.0996 20.6574 26.8234 20.9461 26.4766 20.9463H22.9092V22.4619C22.909 22.8115 22.6328 23.0994 22.2861 23.0996C21.9393 23.0996 21.6623 22.8116 21.6621 22.4619V20.9463H15.8506C15.5689 22.177 14.4936 23.0996 13.2061 23.0996C12.3581 23.0995 11.6077 22.6941 11.1104 22.0703C10.613 22.6938 9.8634 23.0996 9.01562 23.0996C7.51334 23.0995 6.29688 21.8444 6.29688 20.3076C6.29689 19.7459 6.46578 19.2285 6.74512 18.792H5.52344C5.17676 18.7918 4.90063 18.5039 4.90039 18.1543V9.53809C4.90059 9.18849 5.17674 8.9006 5.52344 8.90039H26.4766ZM9.01562 18.792C8.20738 18.7921 7.54496 19.4699 7.54492 20.3076C7.54492 21.1453 8.20735 21.8231 9.01562 21.8232C9.82401 21.8232 10.4873 21.1454 10.4873 20.3076C10.4873 19.4698 9.82399 18.792 9.01562 18.792ZM13.2061 18.792C12.3978 18.7922 11.7354 19.4699 11.7354 20.3076C11.7354 21.1453 12.3978 21.8231 13.2061 21.8232C14.0144 21.8232 14.6777 21.1454 14.6777 20.3076C14.6777 19.4698 14.0144 18.792 13.2061 18.792ZM15.4775 18.792C15.6477 19.0563 15.7778 19.35 15.8516 19.6689H25.8525V18.792H15.4775ZM6.14746 17.5156H25.8525V10.1768H6.14746V17.5156Z" fill="#FF7900" stroke="#FF7900" stroke-width="0.2"/>
<path d="M8.75 12.5H13.25" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round"/>
<path d="M11 15H15.5" stroke="#FF7900" stroke-width="1.5" stroke-linecap="round"/>
</svg>`,
  acknowledgement: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#5B38F8" fill-opacity="0.12"/>
<path d="M15.9982 6C12.9904 6 11.0402 8.01899 8.73371 8.7549C7.79589 9.05413 7.32697 9.20374 7.1372 9.41465C6.94743 9.62556 6.89186 9.93375 6.78072 10.5501C5.59143 17.146 8.1909 23.244 14.3903 25.6175C15.0564 25.8725 15.3894 26 16.0015 26C16.6135 26 16.9466 25.8725 17.6126 25.6175C23.8116 23.2439 26.4086 17.146 25.219 10.5501C25.1078 9.93364 25.0522 9.6254 24.8624 9.41449C24.6726 9.20358 24.2037 9.05405 23.2659 8.75499C20.9585 8.01915 19.0061 6 15.9982 6Z" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13 17C13 17 14 17 15 19C15 19 18.1765 14 21 13" stroke="#5B38F8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  phone: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M9 13C9 9.70017 9 8.05025 10.0251 7.02513C11.0503 6 12.7002 6 16 6C19.2998 6 20.9497 6 21.9749 7.02513C23 8.05025 23 9.70017 23 13V19C23 22.2998 23 23.9497 21.9749 24.9749C20.9497 26 19.2998 26 16 26C12.7002 26 11.0503 26 10.0251 24.9749C9 23.9497 9 22.2998 9 19V13Z" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"/>
<path d="M15 23H17" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13 6L13.089 6.53402C13.2819 7.69129 13.3783 8.26993 13.7752 8.62204C14.1892 8.98934 14.7761 9 16 9C17.2239 9 17.8108 8.98934 18.2248 8.62204C18.6217 8.26993 18.7181 7.69129 18.911 6.53402L19 6" stroke="#2D69F6" stroke-width="1.5" stroke-linejoin="round"/>
</svg>`,
  photo: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#14B8A6" fill-opacity="0.12"/>
<path d="M10.001 21.9745C10.1297 23.2829 10.4205 24.1636 11.0779 24.8209C12.2569 26 14.1546 26 17.9499 26C21.7452 26 23.6429 26 24.8219 24.8209C26.001 23.6419 26.001 21.7442 26.001 17.9489C26.001 14.1536 26.001 12.256 24.8219 11.0769C24.1646 10.4196 23.2839 10.1287 21.9754 10" stroke="#14B8A6" stroke-width="1.5"/>
<path d="M6.00195 14C6.00195 10.2288 6.00195 8.34315 7.17353 7.17157C8.3451 6 10.2307 6 14.002 6C17.7732 6 19.6588 6 20.8304 7.17157C22.002 8.34315 22.002 10.2288 22.002 14C22.002 17.7712 22.002 19.6569 20.8304 20.8284C19.6588 22 17.7732 22 14.002 22C10.2307 22 8.3451 22 7.17353 20.8284C6.00195 19.6569 6.00195 17.7712 6.00195 14Z" stroke="#14B8A6" stroke-width="1.5"/>
<path d="M6.00195 15.1185C6.62097 15.0398 7.24679 15.001 7.87367 15.0023C10.5256 14.9533 13.1126 15.6763 15.173 17.0424C17.084 18.3094 18.4267 20.053 19.002 22" stroke="#14B8A6" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M16.9998 11H17.0088" stroke="#14B8A6" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  signature: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#14B8A6" fill-opacity="0.12"/>
<path d="M26.002 16.6342C22.002 20.1464 21.4298 14.6209 19.3515 15.0164C17.002 15.4635 15.502 20.4443 17.002 20.4443C18.502 20.4443 16.502 14.4999 14.502 16.5554C12.502 18.611 11.8613 21.2945 10.2372 19.3023C2.50195 9.81434 9.00194 2.84994 12.1652 7.45673C14.1672 10.3724 10.502 20.9768 6.00195 25.9999" stroke="#14B8A6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.001 24.9998H23.001" stroke="#14B8A6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  rich_text: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="8" fill="#2D69F6" fill-opacity="0.12"/>
<path d="M11 21H19" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11 16H21" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11 11H16" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,

};

const DRAG_GROUP = "form-fields";
// Columns have their own group: a field dragged from the palette is not a column.
const COLUMN_GROUP = "form-columns";

export default {
  name: "FormTemplateEditor",
  components: { vSelect, VueEditor, DeleteModal, AssignDriversDrawer },
  directives: {
    // Grows a textarea with its content, never dropping below the 40px the other
    // fields in the row use.
    autogrow: {
      inserted(el) {
        const resize = () => {
          el.style.height = "auto";
          el.style.height = `${Math.max(el.scrollHeight, 40)}px`;
        };
        el.__autogrow__ = resize;
        el.addEventListener("input", resize);
        resize();
      },
      componentUpdated(el) {
        if (el.__autogrow__) el.__autogrow__();
      },
      unbind(el) {
        if (el.__autogrow__) el.removeEventListener("input", el.__autogrow__);
      },
    },
  },
  props: {
    // A saved template row, or an empty object for a brand new one.
    template: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editor: {
        id: null,
        name: "",
        status: "draft",
        require_approvals: false,
        access_mode: "all",
        access_members: [],
        access_tags: [],
        sections: [],
      },
      memberOptions: [],
      // The picker edits a copy; the card only changes on Confirm.
      accessModalSelected: [],
      // Assigning: the drawer owns the picker; this only opens it.
      assignDrawer: false,
      // Separate from the drawer's own date: this one edits people already
      // assigned.
      bulkDueDate: new Date(),
      assignedRows: [],
      assignedSearch: "",
      editingRow: null,
      // The assignment waiting on a confirmed delete.
      rowToRemove: null,
      editingDate: null,
      momentFormat: {
        stringify: (date) => (date ? moment(date).format("ll") : ""),
        parse: (value) => (value ? moment(value).toDate() : null),
      },
      // The formatting an instructions block can carry — the same set the
      // recruiting editor offers, minus the parts a form has no use for.
      instructionsToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, 3, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
      // The block whose answers the modal is editing, and a working copy of them.
      optionsBlock: null,
      optionsDraft: [],
      optionsError: "",
      nameDraft: "",
      uid: 0,
      sortables: [],
      errors: {},
    };
  },
  computed: {
    ...mapGetters(["tags", "userCompany", "allDrivers", "rolePermissions"]),
    allowUpdateForms() {
        return this.rolePermissions && this.rolePermissions['Forms'] && this.rolePermissions['Forms'][1].allow
    },
    allTags() {
      return this.tags.map((item) => ({ id: item.id, label: item.add_tag }));
    },
    // Same status logic as the Forms table — only the chip around it differs.
    isPublished() {
      return isPublished(this.editor.status);
    },
    // Same wording as the Templates table, off the same slug — an unknown status
    // falls back to whatever the API called it.
    statusLabel() {
      const key = `formsPage.status_${formStatusKey(this.editor.status)}`;
      const label = this.$t(key);
      return label === key ? this.editor.status : label;
    },
    statusClass() {
      return `status-chip--${formStatusTone(this.editor.status)}`;
    },
    accessModeOptions() {
      return ["all", "specific", "tags", "none"].map((value) => ({
        value,
        text: this.$t(`formsEditor.access${value.charAt(0).toUpperCase()}${value.slice(1)}`),
        hint: this.$t(`formsEditor.accessHint_${value}`),
      }));
    },
    // Four faces is what fits before the row starts pushing the button around.
    stackedAssigned() {
      return this.assignedRows.slice(0, 4);
    },
    hiddenAssignedCount() {
      return Math.max(0, this.assignedRows.length - this.stackedAssigned.length);
    },
    assignedNames() {
      return this.assignedRows.map((row) => row.name).join(", ");
    },
    accessModeHint() {
      const mode = this.accessModeOptions.find((option) => option.value === this.editor.access_mode);
      return mode ? mode.hint : "";
    },
    removeAssignedMessage() {
      return this.$t("formsEditor.confirmRemoveAssigned", {
        name: (this.rowToRemove && this.rowToRemove.name) || "",
      });
    },
    filteredAssigned() {
      const search = this.assignedSearch.trim().toLowerCase();
      if (!search) return this.assignedRows;
      return this.assignedRows.filter((row) => `${row.name}`.toLowerCase().includes(search));
    },
    accessPickerLabel() {
      return this.editor.access_mode === "tags"
        ? this.$t("formsEditor.selectTags")
        : this.$t("formsEditor.selectDrivers");
    },
    accessPickerOptions() {
      return this.editor.access_mode === "tags" ? this.allTags : this.memberOptions;
    },
    // Whatever is currently picked, shown as chips under the button.
    selectedAccessChips() {
      const picked = this.editor.access_mode === "tags" ? this.editor.access_tags : this.editor.access_members;
      const options = this.accessPickerOptions;
      return (picked || []).map((id) => {
        const match = options.find((option) => `${option.id}` === `${id}`);
        return { id, label: match ? match.label : id };
      });
    },
    autoFields() {
      return [
        { key: "user", label: this.$t("formsEditor.autoUser") },
        { key: "submitted_at", label: this.$t("formsEditor.autoSubmittedAt") },
      ];
    },
    mediaCountOptions() {
      return [
        { value: false, text: this.$t("formsEditor.mediaSingle") },
        { value: true, text: this.$t("formsEditor.mediaMultiple") },
      ];
    },
    conditionOptions() {
      return [
        { value: "equals", text: this.$t("formsEditor.conditionEquals") },
        { value: "not_equals", text: this.$t("formsEditor.conditionNotEquals") },
      ];
    },
    fieldTypes() {
      return [
        { value: "instructions", group: "basic", icon: "text-left", label: this.$t("formsEditor.typeInstructions") },
        { value: "text", group: "basic", icon: "fonts", label: this.$t("formsEditor.typeText") },
        { value: "rich_text", group: "basic", icon: "text-paragraph", label: this.$t("formsEditor.typeLongText") },
        { value: "number", group: "basic", icon: "hash", label: this.$t("formsEditor.typeNumber") },
        { value: "phone", group: "basic", icon: "telephone", label: this.$t("formsEditor.typePhone") },
        { value: "date", group: "basic", icon: "calendar", label: this.$t("formsEditor.typeDate") },
        { value: "select", group: "choice", icon: "record-circle", label: this.$t("formsEditor.typeSelect") },
        { value: "radio", group: "choice", icon: "menu-button", label: this.$t("formsEditor.typeRadio") },
        { value: "acknowledgement", group: "choice", icon: "shield-check", label: this.$t("formsEditor.typeAcknowledgement") },
        { value: "photo", group: "media", icon: "image", label: this.$t("formsEditor.typeMedia") },
        { value: "signature", group: "media", icon: "pencil", label: this.$t("formsEditor.typeSignature") },
        { value: "driver", group: "assets", icon: "person", label: this.$t("formsEditor.typeDriver") },
        { value: "vehicle", group: "assets", icon: "truck", label: this.$t("formsEditor.typeVehicle") },
        { value: "trailer", group: "assets", icon: "truck-flatbed", label: this.$t("formsEditor.typeTrailer") },
      ];
    },
    // A column is a question; a read-only text block makes no sense in one.
    columnTypeOptions() {
      return this.columnTypes.map((type) => ({ value: type.value, text: type.label }));
    },
    // The API passes this key through untouched, so the list is ours to define.
    autofillOptions() {
      return [
        { value: null, text: this.$t("formsEditor.selectSource") },
        { value: "vehicles", text: this.$t("formsEditor.typeVehicle") },
        { value: "trailers", text: this.$t("formsEditor.typeTrailer") },
        { value: "drivers", text: this.$t("formsEditor.typeDriver") },
      ];
    },
    columnTypes() {
      return this.fieldTypes.filter((type) => type.value !== "instructions");
    },
  },
  methods: {
    ...mapActions([
      "getFormTemplate",
      "addFormTemplate",
      "updateFormTemplate",
      "publishFormTemplate",
      "unpublishFormTemplate",
      "getAllDrivers",
      "getAllTags",
      "updateFormSubmission",
      "deleteFormSubmission",
      "setNotification",
      "setLoader",
    ]),
    typeMeta(type) {
      return (
        this.fieldTypes.find((entry) => entry.value === type) || {
          value: type,
          group: "basic",
          icon: "fonts",
          label: type,
        }
      );
    },
    typeSvg(type) {
      return TYPE_SVGS[type] || "";
    },
    typeIconStyle(type) {
      const colors = {
        basic: "#2D69F6",
        assets: "#FF7900",
        choice: "#5B38F8",
        media: "#14B8A6",
      };
      const color = colors[this.typeMeta(type).group] || colors.basic;
      return { color, backgroundColor: this.tint(color) };
    },
    // The palette icons sit on a 12% wash of their own colour.
    tint(hex) {
      const value = hex.replace("#", "");
      const r = parseInt(value.slice(0, 2), 16);
      const g = parseInt(value.slice(2, 4), 16);
      const b = parseInt(value.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },
    // A one-line reminder of what the driver will actually do with this field.
    typeHint(type) {
      const hints = {
        driver: "hintDriver",
        vehicle: "hintVehicle",
        trailer: "hintTrailer",
        photo: "hintPhoto",
        signature: "hintSignature",
        acknowledgement: "hintAcknowledgement",
      };
      return hints[type] ? this.$t(`formsEditor.${hints[type]}`) : "";
    },
    // A condition can only test one of the answers the question actually offers.
    valueOptions(block) {
      return [{ value: "", text: this.$t("formsEditor.selectValue") }].concat(
        (block.options || [])
          .map((option) => `${option}`.trim())
          .filter(Boolean)
          .map((option) => ({ value: option, text: option }))
      );
    },
    addConditionalLogic(block) {
      this.$set(block, "conditional_logic", { condition: "equals", value: "", fields: [] });
      this.$nextTick(() => this.initSortable());
    },
    removeConditionalLogic(block) {
      this.$delete(block, "conditional_logic");
      this.$nextTick(() => this.initSortable());
    },
    // Clicking the target adds to the end; dragging drops wherever you release.
    addConditionalField(block, type) {
      const logic = block.conditional_logic;
      if (!logic) return;
      logic.fields.push(this.newBlock(type));
      this.$nextTick(() => this.initSortable());
    },
    removeConditionalField(block, id) {
      const logic = block.conditional_logic;
      if (!logic) return;
      logic.fields = logic.fields.filter((field) => field.id !== id);
    },
    isChoice(type) {
      return CHOICE_TYPES.includes(type);
    },
    isCombo(type) {
      return COMBO_TYPES.includes(type);
    },
    subFieldKeys(type) {
      return type === "driver" ? DRIVER_SUB_FIELD_KEYS : ASSET_SUB_FIELD_KEYS;
    },
    // Ids are the keys answers are filed under, so the client mints them and they
    // never change. The type keeps them readable; the timestamp plus a per-editor
    // counter keeps two fields added in the same millisecond apart.
    nextId(prefix) {
      this.uid += 1;
      return `${prefix}_${Date.now()}${this.uid}`;
    },
    async fetchTemplate() {
      if (!this.template || !this.template.id) {
        this.loadTemplate({});
        return;
      }
      try {
        this.setLoader(true);
        const data = await this.getFormTemplate(this.template.id);
        this.loadTemplate(data || this.template);
      } catch (error) {
        console.error("Error fetching form template:", error);
        this.loadTemplate(this.template);
      } finally {
        this.setLoader(false);
      }
    },
    loadTemplate(template) {
      const definition = Array.isArray(template.definition) ? template.definition : [];
      // Only sections are editable here; a table entry passes through untouched.
      // Sections and tables sit in one ordered list, exactly as they are saved.
      const sections = definition.map((entry) => {
        if (this.isTable(entry)) {
          return {
            ...entry,
            id: entry.id || this.nextId("t"),
            type: "table",
            title: entry.title || this.$t("formsEditor.tableChip"),
            autofill: !!entry.autofill_source,
            autofill_source: entry.autofill_source || null,
            columns: (entry.columns || []).map((column) => this.loadBlock(column)),
          };
        }
        return {
          ...entry,
          id: entry.id || this.nextId("s"),
          title: entry.title || this.$t("formsEditor.sectionChip"),
          blocks: (entry.blocks || []).map((block) => this.loadBlock(block)),
        };
      });
      this.editor = {
        id: template.id || null,
        name: template.name || "",
        status: template.status || "draft",
        require_approvals: template.require_approvals === true,
        access_mode: template.access_mode || "all",
        access_members: (template.access_members || []).map((member) =>
          member && member.id ? `${member.id}` : `${member}`
        ),
        access_tags: (template.access_tags || []).map((tag) => (tag && tag.id ? tag.id : tag)),
        sections: sections.length ? sections : [this.newSection()],
      };
      const saved = (template.access_members || [])
        .filter((member) => member && member.id)
        .map((member) => ({ id: `${member.id}`, label: member.name }));
      this.memberOptions = [
        ...new Map([...saved, ...this.memberOptions].map((option) => [option.id, option])).values(),
      ];
      this.assignedRows = this.mapAssignments(template);
      this.$nextTick(() => this.initSortable());
    },
    loadBlock(block) {
      const type = block.kind === "content" ? "instructions" : block.type;
      const loaded = {
        ...block,
        id: block.id || this.nextId(`b_${type}`),
        type,
        label: block.label || "",
        required: block.required === true,
        body: block.body || "",
        options: Array.isArray(block.options) ? [...block.options] : [],
        multiple: block.multiple === true,
        sub_fields: Array.isArray(block.sub_fields) ? block.sub_fields.map((sub) => ({ ...sub })) : [],
      };
      // Fields revealed by an answer are blocks in their own right.
      const logic = block.conditional_logic;
      if (logic) {
        loaded.conditional_logic = {
          ...logic,
          condition: logic.condition || "equals",
          value: logic.value === null || logic.value === undefined ? "" : logic.value,
          fields: (logic.fields || []).map((field) => this.loadBlock(field)),
        };
      }
      return loaded;
    },
    isTable(entry) {
      return !!entry && (entry.type === "table" || !!entry.columns);
    },
    newTable() {
      return {
        id: this.nextId("t"),
        type: "table",
        title: this.$t("formsEditor.tableChip"),
        columns: [],
        min_rows: 1,
        autofill: false,
        autofill_source: null,
      };
    },
    addTable() {
      this.editor.sections.push(this.newTable());
      this.$nextTick(() => this.initSortable());
    },
    // Changing a column's type drops what the old type owned, so a text column
    // can't keep the options it had as a dropdown.
    setColumnType(column, type) {
      column.type = type;
      if (!this.isChoice(type)) column.options = [];
      if (!this.isCombo(type)) column.sub_fields = [];
    },
    // Turning autofill off clears the source rather than leaving it stored.
    onAutofillToggle(table, enabled) {
      if (!enabled) table.autofill_source = null;
    },
    addColumn(table, type) {
      table.columns.push(this.newBlock(type));
      this.$nextTick(() => this.initSortable());
    },
    removeColumn(table, id) {
      table.columns = table.columns.filter((column) => column.id !== id);
    },
    newSection() {
      return { id: this.nextId("s"), title: this.$t("formsEditor.sectionChip"), blocks: [] };
    },
    newBlock(type) {
      return {
        id: this.nextId(`b_${type}`),
        type,
        label: this.typeMeta(type).label,
        required: false,
        body: "",
        // No blank answer to start with: it rendered as a nameless chip on the
        // row while the modal showed it as an empty box.
        options: [],
        sub_fields: [],
        // Media takes one file unless the builder says otherwise.
        multiple: false,
      };
    },
    addSection() {
      this.editor.sections.push(this.newSection());
      this.$nextTick(() => this.initSortable());
    },
    removeSection(index) {
      this.editor.sections.splice(index, 1);
      this.$nextTick(() => this.initSortable());
    },
    // Clicking a palette item is the shortcut for dragging it to the bottom of
    // the last section.
    addBlock(type) {
      const section = this.editor.sections[this.editor.sections.length - 1];
      if (!section) return;
      section.blocks.push(this.newBlock(type));
    },
    addSectionField(section, type) {
      section.blocks.push(this.newBlock(type));
      this.$nextTick(() => this.initSortable());
    },
    removeBlock(sectionIndex, id) {
      const section = this.editor.sections[sectionIndex];
      if (!section) return;
      section.blocks = section.blocks.filter((block) => block.id !== id);
    },
    removeOption(block, index) {
      block.options.splice(index, 1);
    },
    // Answers are edited in one place, then applied on Save — typing straight
    // into the row made a half-typed answer part of the form.
    openOptionsModal(block) {
      this.optionsBlock = block;
      const options = (block.options || []).slice();
      this.optionsDraft = options.length ? options : ["", ""];
      this.optionsError = "";
      this.$bvModal.show("modal-edit-answers");
    },
    closeOptionsModal() {
      this.$bvModal.hide("modal-edit-answers");
      this.optionsBlock = null;
      this.optionsDraft = [];
    },
    setDraftOption(index, value) {
      this.$set(this.optionsDraft, index, value);
    },
    addDraftOption() {
      this.optionsDraft.push("");
    },
    removeDraftOption(index) {
      this.optionsDraft.splice(index, 1);
    },
    saveOptions() {
      if (!this.optionsBlock) return;
      // Blank rows are how a line gets dropped, so they never reach the block.
      const options = this.optionsDraft.map((option) => `${option}`.trim()).filter(Boolean);
      // A choice of one isn't a choice.
      if (options.length < 2) {
        this.optionsError = this.$t("formsEditor.optionsMin");
        return;
      }
      this.optionsBlock.options = options;
      this.closeOptionsModal();
    },
    hasSubField(block, key) {
      return (block.sub_fields || []).some((sub) => sub.key === key);
    },
    toggleSubField(block, key, checked) {
      if (checked) block.sub_fields.push({ key, required: false });
      else block.sub_fields = block.sub_fields.filter((sub) => sub.key !== key);
    },
    // The drawer scrolls on its own, so the page behind it holds still while it
    // is open — two scrollbars at once is what made it feel loose.
    lockPageScroll(locked) {
      document.body.style.overflow = locked ? "hidden" : "";
    },
    async openAssignDrawer() {
      this.bulkDueDate = new Date();
      this.assignedSearch = "";
      this.editingRow = null;
      this.assignDrawer = true;
      await this.loadAssigned();
    },
    mapAssignments(template) {
      return (((template || {}).assignments) || []).map((row) => ({
        id: row.submission_id,
        member_id: row.member && row.member.id,
        name: (row.member && row.member.name) || "-",
        due_date: row.due_date,
        status: row.status,
        is_due: row.is_due,
      }));
    },
    // Who already has this form, straight off the template's own `assignments`.
    async loadAssigned() {
      try {
        const data = await this.getFormTemplate(this.editor.id);
        this.assignedRows = this.mapAssignments(data);
      } catch (error) {
        this.assignedRows = [];
        console.error("Error loading assigned users:", error);
      }
    },
    // Everyone at once, rather than opening each row in turn.
    async applyDueDateToAll() {
      if (!this.assignedRows.length) return;
      const dueDate = this.bulkDueDate ? moment(this.bulkDueDate).format("YYYY-MM-DD") : null;
      try {
        this.setLoader(true);
        await Promise.all(
          this.assignedRows.map((row) =>
            this.updateFormSubmission({ id: row.id, payload: { due_date: dueDate } })
          )
        );
        this.editingRow = null;
        await this.loadAssigned();
        await this.setNotification({
          msg: this.$t("formsEditor.dueDatesUpdated"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    startEditRow(row) {
      this.editingRow = row.id;
      this.editingDate = pickerDate(row.due_date) || null;
    },
    // A due date is a plain edit on that person's row, so it PATCHes it.
    async saveRowDue(row) {
      try {
        this.setLoader(true);
        const dueDate = this.editingDate ? moment(this.editingDate).format("YYYY-MM-DD") : null;
        await this.updateFormSubmission({ id: row.id, payload: { due_date: dueDate } });
        this.editingRow = null;
        await this.loadAssigned();
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    confirmRemoveAssigned(row) {
      this.rowToRemove = row;
    },
    // Taking the form off somebody deletes what they filled in with it, so it is
    // confirmed like any other delete.
    async removeAssigned(confirmed) {
      const row = this.rowToRemove;
      this.rowToRemove = null;
      if (!confirmed || !row) return;
      try {
        this.setLoader(true);
        await this.deleteFormSubmission(row.id);
        await this.loadAssigned();
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    formatDate(date) {
      return date ? moment(date).format("ll") : "";
    },
    initials(name) {
      return `${name || ""}`
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
    },
    // A stable colour per person, from the palette the builder already uses.
    avatarColor(name) {
      const colors = ["#5B38F8", "#FF7900", "#14B8A6", "#F8AFBA", "#2D69F6"];
      const seed = `${name || ""}`.split("").reduce((total, char) => total + char.charCodeAt(0), 0);
      return colors[seed % colors.length];
    },
    openAccessModal() {
      const picked = this.editor.access_mode === "tags" ? this.editor.access_tags : this.editor.access_members;
      this.accessModalSelected = [...(picked || [])];
      this.$bvModal.show("modal-select-access");
    },
    closeAccessModal() {
      this.$bvModal.hide("modal-select-access");
    },
    // Tags are already loaded; drivers are searched as you type.
    onAccessSearch(searchText) {
      if (this.editor.access_mode !== "tags") this.searchMembers(searchText);
    },
    confirmAccessSelection() {
      if (this.editor.access_mode === "tags") this.editor.access_tags = [...this.accessModalSelected];
      else this.editor.access_members = [...this.accessModalSelected];
      this.closeAccessModal();
    },
    // Drivers, not team members: get-all-users answers with role=no_drivers,
    // which is exactly the people this picker must not offer.
    async searchMembers(searchText) {
      try {
        await this.getAllDrivers(`page=1&page_size=20&name=${searchText || ""}`);
        const options = (this.allDrivers || []).map((driver) => {
          const user = driver.user || {};
          return {
            id: `${driver.id}`,
            label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || driver.name || `${driver.id}`,
          };
        });
        // Keep the current picks behind the results so their labels survive a new search.
        const picked = this.accessModalSelected.length ? this.accessModalSelected : this.editor.access_members;
        const selected = this.memberOptions.filter((option) => picked.includes(option.id));
        this.memberOptions = [...new Map([...selected, ...options].map((item) => [item.id, item])).values()];
      } catch (error) {
        // Worth seeing: an empty picker with a silent 403 behind it reads as a
        // bug in the list rather than a permissions problem.
        console.error("Error searching drivers:", error);
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      }
    },
    definitionPayload() {
      const sections = this.editor.sections.map((section, sectionIndex) => this.isTable(section)
        ? {
            ...section,
            id: section.id,
            type: "table",
            title: section.title || "",
            order: sectionIndex + 1,
            min_rows: section.min_rows || 1,
            autofill_source: section.autofill ? section.autofill_source || null : null,
            autofill: undefined,
            columns: section.columns.map((column, columnIndex) => this.blockPayload(column, columnIndex)),
          }
        : ({
        ...section,
        id: section.id,
        type: "section",
        title: section.title || "",
        order: sectionIndex + 1,
        blocks: section.blocks.map((block, blockIndex) => this.blockPayload(block, blockIndex)),
      }));
      return sections;
    },
    // Every question in the form: section blocks, table columns, and the fields a
    // condition reveals.
    allQuestions() {
      const questions = [];
      const walk = (block) => {
        questions.push(block);
        const logic = block.conditional_logic;
        if (logic) (logic.fields || []).forEach(walk);
      };
      this.editor.sections.forEach((section) => {
        (this.isTable(section) ? section.columns : section.blocks).forEach(walk);
      });
      return questions;
    },
    blockPayload(block, index) {
      const isContent = CONTENT_TYPES.includes(block.type);
      const entry = {
        // Anything this builder doesn't edit — help_text, placeholder, min, max,
        // width — rides along untouched.
        ...block,
        id: block.id,
        kind: isContent ? "content" : "field",
        type: block.type,
        label: (block.label || "").trim(),
        order: index + 1,
        required: isContent ? false : block.required === true,
      };
      // Only the keys this type actually uses go up, so a type change can't
      // leave stale options, sub-fields or conditions behind.
      if (isContent) entry.body = block.body || "";
      else delete entry.body;
      if (this.isChoice(block.type)) {
        entry.options = (block.options || []).map((option) => `${option}`.trim()).filter(Boolean);
      } else {
        delete entry.options;
      }
      if (block.type === "photo") entry.multiple = block.multiple === true;
      else delete entry.multiple;
      if (this.isCombo(block.type)) {
        entry.sub_fields = (block.sub_fields || []).map((sub) => ({
          key: sub.key,
          required: sub.required === true,
        }));
      } else {
        delete entry.sub_fields;
      }
      const logic = block.conditional_logic;
      if (this.isChoice(block.type) && logic) {
        entry.conditional_logic = {
          ...logic,
          condition: logic.condition || "equals",
          value: logic.value,
          fields: (logic.fields || []).map((field, fieldIndex) => this.blockPayload(field, fieldIndex)),
        };
      } else {
        delete entry.conditional_logic;
      }
      return entry;
    },
    templatePayload() {
      const mode = this.editor.access_mode;
      return {
        name: this.editor.name.trim(),
        require_approvals: this.editor.require_approvals,
        access_mode: mode,
        // Only the list the mode uses is sent — switching modes can't leak the other.
        access_members: mode === "specific" ? this.editor.access_members : [],
        access_tags: mode === "tags" ? this.editor.access_tags : [],
        definition: this.definitionPayload(),
      };
    },
    async saveTemplate() {
      if (!this.editor.name.trim()) {
        await this.setNotification({
          msg: this.$t("formsEditor.templateNameRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      const blocks = this.allQuestions();
      // Every choice question needs answers to choose between.
      const thin = blocks.find(
        (block) =>
          this.isChoice(block.type) &&
          (block.options || []).map((option) => `${option}`.trim()).filter(Boolean).length < 2
      );
      if (thin) {
        await this.setNotification({
          msg: this.$t("formsEditor.optionsMinFor", { label: thin.label || thin.type }),
          type: "error",
          color: "red",
        });
        return;
      }
      if (!blocks.length) {
        await this.setNotification({
          msg: this.$t("formsEditor.fieldsRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      // A blank label saves an unreadable question, so ask for it here instead.
      // Instructions carry a body rather than a label, so they are exempt.
      if (blocks.some((block) => !CONTENT_TYPES.includes(block.type) && !(block.label || "").trim())) {
        await this.setNotification({
          msg: this.$t("formsEditor.fieldLabelRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      try {
        this.setLoader(true);
        const payload = this.templatePayload();
        if (this.editor.id) await this.updateFormTemplate({ id: this.editor.id, payload });
        else await this.addFormTemplate(payload);
        this.$emit("close");
        await this.setNotification({
          msg: this.$t("formsEditor.templateSaved"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    async togglePublish() {
      const publishing = !this.isPublished;
      try {
        this.setLoader(true);
        const data = publishing
          ? await this.publishFormTemplate(this.editor.id)
          : await this.unpublishFormTemplate(this.editor.id);
        this.editor.status = (data && data.status) || (publishing ? "published" : "draft");
        await this.setNotification({
          msg: publishing ? this.$t("formsEditor.published") : this.$t("formsEditor.unpublished"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        // Publish refuses a form that isn't ready and answers with the reasons.
        const problems = error && error.problems;
        await this.setNotification({
          msg: Array.isArray(problems) && problems.length ? problems.join(" ") : this.apiError(error),
          type: "error",
          color: "red",
        });
      } finally {
        this.setLoader(false);
      }
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error && error.error) return error.error;
      if (error && error.detail) return error.detail.error || error.detail;
      const firstField = error && Object.values(error)[0];
      if (Array.isArray(firstField)) return firstField[0];
      return firstField || this.$t("formsEditor.templateSaveFailed");
    },
    openEditNameModal() {
      this.nameDraft = this.editor.name;
      this.errors = {};
      this.$bvModal.show("modal-edit-form-name");
    },
    closeEditNameModal() {
      this.$bvModal.hide("modal-edit-form-name");
    },
    async saveTemplateName() {
      const name = this.nameDraft.trim();
      if (!name) {
        this.errors = { templateName: [this.$t("formsEditor.templateNameRequired")] };
        return;
      }
      const previousName = this.editor.name;
      this.editor.name = name;
      // A new template has no record yet — its name goes up with everything else on Save.
      if (!this.editor.id) {
        this.closeEditNameModal();
        return;
      }
      try {
        this.setLoader(true);
        await this.updateFormTemplate({ id: this.editor.id, payload: { name } });
        this.closeEditNameModal();
        await this.setNotification({
          msg: this.$t("formsEditor.templateSaved"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        // Names are unique per company, so a clash is a realistic failure — put the
        // old name back and keep the modal open with the reason.
        this.editor.name = previousName;
        this.errors = { templateName: [this.apiError(error)] };
      } finally {
        this.setLoader(false);
      }
    },
    destroySortable() {
      this.sortables.forEach((instance) => {
        try {
          instance.destroy();
        } catch (e) {
          /* noop */
        }
      });
      this.sortables = [];
    },
    // Sortable moves the real DOM, but Vue owns it — put the node back where it
    // came from and let the data drive what actually renders.
    undoSortableDom(evt) {
      if (evt.item.parentNode) evt.item.parentNode.removeChild(evt.item);
      if (evt.from === this.$refs.palette) return;
      const ref = evt.from.children[evt.oldIndex] || null;
      evt.from.insertBefore(evt.item, ref);
    },
    initSortable() {
      if (!window.Sortable) return;
      this.destroySortable();
      if (this.$refs.palette) {
        this.sortables.push(
          window.Sortable.create(this.$refs.palette, {
            // Clone out of the palette: the list itself never changes.
            group: { name: DRAG_GROUP, pull: "clone", put: false },
            sort: false,
            animation: 150,
          })
        );
      }
      // Only a section's body takes drops; a table's columns are added by name.
      const bodies = (this.$refs.sectionBodies || []).filter(
        (body) => body.dataset.section !== undefined && body.dataset.columns === undefined
      );
      bodies.forEach((body) => {
        this.sortables.push(
          window.Sortable.create(body, {
            group: DRAG_GROUP,
            animation: 150,
            handle: ".drag-handle",
            ghostClass: "field-row-ghost",
            onAdd: (evt) => this.onFieldDropped(evt),
            onUpdate: (evt) => this.onFieldMoved(evt),
          })
        );
      });
      const condBodies = this.$refs.condBodies || [];
      condBodies.forEach((body) => {
        this.sortables.push(
          window.Sortable.create(body, {
            group: DRAG_GROUP,
            animation: 150,
            handle: ".drag-handle",
            ghostClass: "field-row-ghost",
            onAdd: (evt) => this.onFieldDropped(evt),
            onUpdate: (evt) => this.onFieldMoved(evt),
          })
        );
      });
      const columnBodies = this.$refs.columnBodies || [];
      columnBodies.forEach((body) => {
        this.sortables.push(
          window.Sortable.create(body, {
            group: COLUMN_GROUP,
            animation: 150,
            handle: ".drag-handle",
            ghostClass: "field-row-ghost",
            onUpdate: (evt) => this.onColumnMoved(evt),
          })
        );
      });
      if (this.$refs.sectionsList) {
        this.sortables.push(
          window.Sortable.create(this.$refs.sectionsList, {
            animation: 150,
            handle: ".section-drag",
            draggable: ".section-card",
            onEnd: (evt) => this.onSectionMoved(evt),
          })
        );
      }
    },
    sectionAt(el) {
      if (!el || !el.dataset) return null;
      const section = this.editor.sections[Number(el.dataset.section)];
      return section && !this.isTable(section) ? section : null;
    },
    // A drop target is either a section body or the field list inside a
    // condition; both hold plain arrays of blocks.
    listAt(el) {
      if (!el || !el.dataset) return null;
      if (el.dataset.section !== undefined) {
        const section = this.sectionAt(el);
        return section ? section.blocks : null;
      }
      if (!el.dataset.cond) return null;
      const [sectionIndex, blockId] = el.dataset.cond.split("|");
      const section = this.editor.sections[Number(sectionIndex)];
      const block = section && section.blocks.find((entry) => entry.id === blockId);
      return block && block.conditional_logic ? block.conditional_logic.fields : null;
    },
    // A field arrived from the palette (a new block) or from another list.
    onFieldDropped(evt) {
      const target = this.listAt(evt.to);
      if (!target) return;
      this.undoSortableDom(evt);
      if (evt.from === this.$refs.palette) {
        const type = evt.item.dataset.type;
        if (!type) return;
        target.splice(evt.newIndex, 0, this.newBlock(type));
        return;
      }
      const source = this.listAt(evt.from);
      if (!source) return;
      const id = evt.item.dataset.id;
      const index = source.findIndex((block) => block.id === id);
      if (index === -1) return;
      const [moved] = source.splice(index, 1);
      target.splice(evt.newIndex, 0, moved);
    },
    onFieldMoved(evt) {
      const list = this.listAt(evt.to);
      if (!list || evt.oldIndex === evt.newIndex) return;
      this.undoSortableDom(evt);
      const [moved] = list.splice(evt.oldIndex, 1);
      list.splice(evt.newIndex, 0, moved);
    },
    onColumnMoved(evt) {
      const table = this.editor.sections[Number(evt.to.dataset.columns)];
      if (!table || !this.isTable(table) || evt.oldIndex === evt.newIndex) return;
      this.undoSortableDom(evt);
      const columns = [...table.columns];
      const [moved] = columns.splice(evt.oldIndex, 1);
      columns.splice(evt.newIndex, 0, moved);
      table.columns = columns;
    },
    onSectionMoved(evt) {
      // Read both positions off the cards themselves: Sortable's indices count
      // only what it drags, and this container also holds the General card and
      // the Add section / Save rows, so they don't line up with `sections`.
      const cards = Array.from(evt.to.querySelectorAll(".section-card")).filter(
        (card) => card.parentNode === evt.to
      );
      const newIndex = cards.indexOf(evt.item);
      const oldIndex = Number(evt.item.dataset.index);
      this.undoSortableDom(evt);
      const sections = [...this.editor.sections];
      if (newIndex < 0 || !sections[oldIndex] || oldIndex === newIndex) return;
      const [moved] = sections.splice(oldIndex, 1);
      sections.splice(Math.min(newIndex, sections.length), 0, moved);
      this.editor.sections = sections;
      // The section bodies are re-created in a new order, so rebind their handlers.
      this.$nextTick(() => this.initSortable());
    },
  },
  created() {
    this.fetchTemplate();
    this.getAllTags(this.userCompany);
    // Seed the driver pickers so they open on a list rather than "type to search".
    this.searchMembers("");
  },
  beforeDestroy() {
    this.destroySortable();
    // Leaving with the drawer open must not leave the page unscrollable.
    this.lockPageScroll(false);
  },
};
</script>
<style scoped>
/* ── HEADER ── */
/* Same badge as the Forms list, which follows DVIR's. */
.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 4px;
  color: #ffffff;
  font-size: 14px;
}
.status-chip--secondary {
  background-color: #f4f4f4 !important;
  border: 1px solid #eaeaea !important;
  color: black !important;
}
.status-chip--success {
  background-color: #2cce89;
}

/* ── ASSIGN DRAWER ── */
.assign-drawer >>> .b-sidebar {
  width: 443px;
  max-width: 100%;
}
.assign-body {
  padding: 24px 16px;
  text-align: left;
}
/* Everything the Access card offers reads across one line. */
.access-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  text-align: left;
}
.access-title {
  white-space: nowrap;
}
.access-control {
  width: 220px;
  margin: 0;
}
/* v-select builds its own control, so the height has to be asked for. */
.access-members >>> .vs__dropdown-toggle {
  min-height: 40px;
  background-color: #ffffff;
}
.access-hint {
  max-width: 320px;
}
/* The summary chip on the Access card: faces, label, count. */
.assigned-summary {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  background-color: #ffffff;
  cursor: pointer;
}
.assigned-summary:hover {
  border-color: #2d69f6;
}
.avatar-stack {
  display: flex;
  align-items: center;
  margin-right: 8px;
}
.stack-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #ffffff;
  color: #ffffff;
  font-size: 12px;
  flex: none;
}
.stack-avatar + .stack-avatar {
  margin-left: -12px;
}
/* Whatever the stack could not fit. */
.stack-avatar--more {
  background-color: #f2f4f9;
  color: #2d69f6;
  font-size: 11px;
}
.assigned-summary-label {
  margin-right: 8px;
  font-size: 14px;
  color: #6c757d;
  white-space: nowrap;
}
.assigned-title {
  font-size: 12px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6c757d;
}
.assigned-count {
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 16px;
  background-color: rgba(45, 105, 246, 0.12);
  border: 1px solid #e0e0e0;
  color: #2d69f6;
  font-size: 12px;
  text-align: center;
}
.assigned-list {
  max-height: calc(100vh - 430px);
  min-height: 120px;
  overflow-y: auto;
}
.assign-card {
  padding: 8px;
  border-radius: 0.25rem;
  border: 1px solid transparent;
}
/* The row being edited lifts out of the list. */
.assign-card--editing {
  background: #f8f9fb;
  border-color: #e0e0e0;
}
.assign-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 8px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 14px;
  flex: none;
}
.assign-name {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
}
.assign-due {
  font-size: 12px;
  color: #6c757d;
}
.assign-date {
  width: 200px;
}
/* The picker takes the room that's left; the label never wraps. */
.bulk-date {
  flex: 1 1 auto;
  min-width: 0;
}
.apply-all {
  flex: none;
  white-space: nowrap;
}
.min-w-0 {
  min-width: 0;
}

/* ── ACCESS PICKER ── */
.selected-access-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}
.access-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: rgba(45, 105, 246, 0.1);
  border: 1px solid rgba(45, 105, 246, 0.5);
  color: #2d69f6;
  font-size: 11px;
  font-weight: 500;
}

/* ── EDITOR SETTINGS ── */
.setting-card {
  height: 100%;
  padding: 1rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.setting-card h4 {
  margin-bottom: 1rem;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6c757d;
}
.chk-row {
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}
.chk-row:last-of-type {
  border-bottom: none;
}
.chk-label {
  display: block;
  font-size: 13px;
  color: #212529;
}
.chk-sub {
  display: block;
  margin-top: 1px;
  font-size: 11px;
  color: #6c757d;
}
/* The access picker: a scrolling list of names, ticked in place. */
/* Takes the height the drawer has going spare, so a long driver list shows as
   many names as fit rather than scrolling inside a short box. */
.access-picker {
  max-height: calc(100vh - 440px);
  min-height: 280px;
  overflow-y: auto;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}
.access-row {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f2f2;
}
.access-row:last-child {
  border-bottom: none;
}
.media-count-select {
  display: block;
  width: 100%;
  text-align: left;
}
/* ── AUTO-COLLECTED ── */
/* Dashed and greyed to read as "not yours to edit", unlike the section cards. */
.general-card {
  margin-bottom: 12px;
  padding: 1rem;
  text-align: left;
  background: #f8f9fb;
  border: 1px dashed #c8d0e4;
  border-radius: 0.25rem;
}
.general-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-right: 8px;
  border-radius: 6px;
  background-color: #e2e6f0;
  /* Same ink as the "General" heading beside it. */
  color: #212529;
  flex: none;
}
.general-title {
  font-size: 13px;
  color: #212529;
}
.general-tag {
  padding: 2px 8px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c757d;
}
.general-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #6c757d;
}
.auto-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.auto-name {
  font-size: 13px;
  color: #212529;
  text-decoration: underline dotted;
}
.auto-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 16px;
  background: #f8f9fb;
  border: 1px solid #e0e0e0;
  font-size: 11px;
  color: #6c757d;
  white-space: nowrap;
}

/* ── LAYOUT: PALETTE LEFT, FORM RIGHT ── */
.editor-layout {
  display: flex;
  /* Stretch, not flex-start: the palette column has to be as tall as the form
     for its sticky card to have anywhere to travel. */
  align-items: stretch;
  gap: 12px;
}
.palette-col {
  flex: none;
  width: 200px;
}
/* Follows the page down, so a field can be dragged out of it however far the
   form is scrolled. A long list scrolls inside the card rather than past it. */
.palette-card {
  position: sticky;
  /* Clears the app's fixed 64px header (#wrapper's padding-top), otherwise the
     top of the list sits underneath it. */
  top: 76px;
  /* Fills the viewport instead of ending where the list does, so no bare column
     shows under it once the form is taller than the palette. */
  height: calc(100vh - 96px);
  overflow-y: auto;
}
.canvas-col {
  flex: 1 1 auto;
  min-width: 0;
  /* A column at least a screen tall, so Save/Close drop to the bottom of the
     view on a short form instead of floating under the last card. */
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 96px);
}
@media (max-width: 767px) {
  .editor-layout {
    display: block;
  }
  .palette-col {
    width: 100%;
    margin-bottom: 12px;
  }
  .palette-card {
    position: static;
  }
}

/* ── PALETTE ── */
.palette-list {
  display: flex;
  flex-direction: column;
}
.palette-item {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 4px; 
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  text-align: left;
  cursor: grab;
}
.palette-item:hover {
  border-color: #2d69f6;
  background-color: #f8f9fb;
}
.palette-item:active {
  cursor: grabbing;
}
/* Supplied artwork brings its own tinted box, so it only needs sizing. */
.type-svg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex: none;
}
.type-svg >>> svg {
  width: 26px;
  height: 26px;
}
.type-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-right: 8px;
  border-radius: 6px;
  flex: none;
}

/* ── SECTION CARDS ── */
.section-card {
  margin-bottom: 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
}
.section-chip {
  padding: 6px 12px;
  border-radius: 0.25rem;
  background-color: #f8f8fa;
  border: 1px solid #e0e0e0;
  color: #a8b2cc;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
}
.section-body-wrap {
  position: relative;
}
/* Height so an empty section is still a drop target; the hint sits over it. */
.section-body {
  min-height: 76px;
}
.section-foot {
  padding: 12px;
  border-top: 1px solid #e0e0e0;
}
.empty-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0;
  padding: 26px 12px;
  text-align: center;
  font-size: 12px;
  color: #6c757d;
  pointer-events: none;
}
.add-row {
  display: flex;
  gap: 12px;
}
/* Half each, whatever their labels are. */
.add-row > .dashed-btn {
  flex: 1 1 50%;
  min-width: 0;
}
@media (max-width: 575px) {
  .add-row {
    flex-direction: column;
  }
}
.dashed-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 24px;
  background: #ffffff;
  border: 1px dashed #c8d0e4;
  border-radius: 0.25rem;
  color: #212529;
  font-size: 14px;
}
/* Tint the box on hover, not the label — blue text read as a link rather than
   a button. */
.dashed-btn:hover {
  border-color: #2d69f6;
  background-color: rgba(45, 105, 246, 0.05);
}

/* ── FIELD ROWS ── */
.field-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}
.field-row:last-child {
  border-bottom: none;
}
.field-row-ghost {
  opacity: 0.4;
  background-color: rgba(45, 105, 246, 0.06);
}
.drag-handle {
  display: inline-flex;
  align-items: center;
  height: 38px;
  color: #c4c4c4;
  cursor: grab;
  flex: none;
}
.drag-handle:active {
  cursor: grabbing;
}
.field-row .type-icon,
.field-row .type-svg {
  margin-top: 6px;
}
.field-main {
  flex: 1 1 auto;
  min-width: 0;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: #6c757d;
}
.required-chk {
  flex: none;
  margin-top: 8px;
  white-space: nowrap;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 38px;
  color: #ff0000;
  cursor: pointer;
  flex: none;
}
/* The rich-text body sits inside a field row, so it stays compact. */
.instructions-editor >>> .ql-container {
  min-height: 90px;
  font-size: 13px;
}
.instructions-editor >>> .ql-toolbar,
.instructions-editor >>> .ql-container {
  border-color: #e0e0e0;
}
.field-main textarea {
  min-height: 40px;
  overflow-y: hidden;
  resize: none;
}

/* ── CHOICE OPTIONS ── */
.option-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.option-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 0.25rem;
  background-color: rgba(45, 105, 246, 0.1);
  border: 1px solid rgba(45, 105, 246, 0.5);
  color: #2d69f6;
  font-size: 11px;
}
.add-option {
  color: #2d69f6;
  text-decoration: none;
}

/* ── TABLE PREVIEW ── */
.table-preview-wrap {
  padding: 16px 12px;
  border-bottom: 1px solid #e0e0e0;
}
.table-scroll {
  overflow-x: auto;
}
.table-preview {
  width: 100%;
  border-collapse: collapse;
}
.table-preview th,
.table-preview td {
  min-width: 140px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  font-size: 13px;
  color: #212529;
}
/* Empty rows: they only show the shape the driver will fill in. */
.table-preview td {
  height: 44px;
}
.autofill-select {
  width: 200px;
}
/* Full width, so a column's type reads under its label the way a field's does. */
.column-type {
  width: 100%;
}

/* ── CONDITIONAL LOGIC ── */
.cond-card {
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.cond-title {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
}
.cond-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.cond-label {
  flex: none;
  font-size: 12px;
  color: #6c757d;
}
/* The dashed rule the design runs between the label and its control. */
.cond-line {
  flex: 1 1 auto;
  border-top: 1px dashed #e0e0e0;
}
.cond-control {
  flex: none;
  width: 260px;
}
.cond-body-wrap {
  position: relative;
  margin-top: 12px;
  margin-bottom: 12px;
  border-top: 1px solid #e0e0e0;
}
/* The picker hangs off a dropdown so clicking outside closes it. Fixed width and
   two columns: fifteen types in one column made a menu taller than the page. */
.field-picker-toggle >>> .field-picker-menu {
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.field-picker {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px;
}
.field-picker-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  font-size: 13px;
  line-height: 1.2;
  text-align: left;
}
.field-picker-item .type-icon {
  width: 24px;
  height: 24px;
  margin-right: 6px;
}
.field-picker-item:hover {
  border-color: #2d69f6;
  background-color: #f8f9fb;
}
@media (max-width: 575px) {
  .field-picker {
    grid-template-columns: 1fr;
  }
}
/* Height so an empty condition is still a drop target. */
.cond-body {
  min-height: 68px;
}
@media (max-width: 767px) {
  .cond-control {
    width: 140px;
  }
}

/* ── COMBO SUB-FIELDS ── */
/* The extra details a driver/vehicle/trailer field can also collect read as one
   group, so they sit in their own card rather than loose under the label. */
.sub-fields {
  margin-top: 8px;
  padding: 12px;
  background: #f8f9fb;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.sub-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 4px 12px;
}
</style>
